import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, LogIn } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) nav("/admin", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await login(email.trim(), password);
      nav("/admin", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-10">
      <div className="absolute inset-0 -z-10">
        <img
          src="/images/hero.jpg"
          alt=""
          className="h-full w-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/85 to-background" />
        <div className="absolute inset-0 bg-grain opacity-30" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md rounded-2xl border border-gold/20 bg-card/70 p-7 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:p-9"
      >
        <p className="text-[0.65rem] uppercase tracking-[0.32em] text-gold">
          Ταβέρνα Βασίλης
        </p>
        <h1 className="mt-2 font-display text-3xl italic text-foreground sm:text-4xl">
          Είσοδος Διαχειριστή
        </h1>
        <div className="mt-4 h-px w-16 divider-gold" />

        <form onSubmit={submit} className="mt-7 space-y-4">
          <label className="block">
            <span className="text-[0.65rem] uppercase tracking-[0.25em] text-gold-muted">
              Email
            </span>
            <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-border/80 bg-background/40 px-3.5 focus-within:border-gold/60">
              <Mail className="h-4 w-4 text-gold-muted" strokeWidth={1.6} />
              <input
                data-testid="login-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent py-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/50"
                placeholder="admin@taverna-vassilis.gr"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-[0.65rem] uppercase tracking-[0.25em] text-gold-muted">
              Κωδικός
            </span>
            <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-border/80 bg-background/40 px-3.5 focus-within:border-gold/60">
              <Lock className="h-4 w-4 text-gold-muted" strokeWidth={1.6} />
              <input
                data-testid="login-password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="flex-1 bg-transparent py-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/50"
                placeholder="••••••••"
              />
            </div>
          </label>

          {err && (
            <div
              data-testid="login-error"
              className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            >
              {err}
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-b from-gold to-gold-muted px-4 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-background shadow-[0_4px_18px_-4px_rgba(212,170,90,0.55)] transition-all hover:brightness-110 disabled:opacity-50"
          >
            <LogIn className="h-3.5 w-3.5" strokeWidth={2} />
            {loading ? "Είσοδος…" : "Είσοδος"}
          </button>
        </form>

        <p className="mt-6 text-center text-[0.6rem] uppercase tracking-[0.25em] text-muted-foreground">
          Μόνο εξουσιοδοτημένο προσωπικό
        </p>
      </motion.div>
    </div>
  );
}
